import React, { useEffect, useRef, useState } from "react";
import { AuthenticatedTemplate, useMsal } from "@azure/msal-react";
import { DataGrid, GridActionsCellItem, GridColDef, GridSlots, GridToolbarContainer } from "@mui/x-data-grid";
import dayjs from "dayjs";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { FormOneBStatus } from "../models/FormOneBStatus";
import {
    Alert,
    AlertProps,
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControl,
    FormControlLabel,
    FormLabel,
    Grid,
    InputLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    Snackbar,
    TextField,
    Tooltip,
    Typography
} from "@mui/material";
import { appConfig } from "../appConfig";
import { AppRole, getAppRole } from "../utils/AppRole";
import { loginRequest } from "../authConfig";
import AddIcon from "@mui/icons-material/Add";
import PreviewIcon from "@mui/icons-material/Preview";
import ArchiveIcon from '@mui/icons-material/Archive';
import InputMask from "react-input-mask";
import { DatePicker } from "@mui/x-date-pickers";
import { v4 as uuid } from "uuid";
import { PoolingAdministrator } from "../components/PoolingAdministrator";
import { PoolingAdministratorInfo } from "../models/Constants";
import CustomDataGridFooter from "../components/CustomDataGridFooter";

interface AddToolbarProps {
    setIsEditMode: (isOpen: boolean) => void;
    setRowEditId: (id: string) => void;
}

const effectiveDateAddDays = 5;

function AddToolbar(props: AddToolbarProps) {
    const { setRowEditId, setIsEditMode } = props;
    const handleClick = () => {
        setIsEditMode(true);
        setRowEditId("");
    }

    return (
        <GridToolbarContainer>
            <Button color="primary" startIcon={<AddIcon />} onClick={handleClick}>
                Add
            </Button>
        </GridToolbarContainer>
    );
};

export function AdminFormOneB() {
    const initialRequest = {
        ...PoolingAdministratorInfo,
        requestType: "Activation",
    }

    const columns: GridColDef<(typeof rows)[number]>[] = [
        {
            field: "referenceNumber",
            headerName: "Ref #",
            width: 150,
        },
        {
            field: "entityName",
            headerName: "Carrier",
            width: 250,
        },
        {
            field: "blockRange",
            headerName: "Block Range",
            width: 250,
        },
        {
            field: "createdAt",
            headerName: "Created At",
            type: "date",
            width: 200,
            valueFormatter: (value) =>
                dayjs(value as string).format('YYYY-MM-DD hh:mm A')
        },
        {
            field: "displayFormOneBStatus",
            headerName: "Status",
            width: 150
        },
        {
            field: "actions",
            type: "actions",
            headerName: "",
            width: 100,
            cellClassName: "actions",
            getActions: ({ id }) => {
                const r = rows.find(x => x.id === id);
                if (r && (r.formOneBStatus == FormOneBStatus.Archived || r.formOneBStatus == FormOneBStatus.Received)) {
                    return [
                        <Tooltip title="View">
                            <GridActionsCellItem
                                icon={<PreviewIcon />}
                                label="View"
                                className="textPrimary"
                                onClick={handlePreviewClick(id.toString())}
                                color="inherit"
                            /></Tooltip>,
                        <Tooltip title="Archive">
                            <GridActionsCellItem
                                icon={<ArchiveIcon />}
                                label="Archive"
                                className="textPrimary"
                                onClick={handleArchiveClick(id.toString())}
                                color="inherit"
                                disabled
                            /></Tooltip>
                    ];
                }

                return [
                    <Tooltip title="View">
                        <GridActionsCellItem
                            icon={<PreviewIcon />}
                            label="View"
                            className="textPrimary"
                            onClick={handlePreviewClick(id.toString())}
                            color="inherit"
                        /></Tooltip>,
                    <Tooltip title="Archive">
                        <GridActionsCellItem
                            icon={<ArchiveIcon />}
                            label="Archive"
                            className="textPrimary"
                            onClick={handleArchiveClick(id.toString())}
                            color="inherit"
                        /></Tooltip>
                ];
            },
        },
    ];

    const { instance } = useMsal();
    const [rows, setRows] = useState<any>([]);
    const [accessToken, setAccessToken] = useState("");
    const [loading, setLoading] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [rowEditId, setRowEditId] = useState("");
    const [formRequest, setFormRequest] = useState(initialRequest);
    const [carriers, setCarriers] = useState([]);
    const [selectedCarrierId, setSelectedCarrierId] = useState("");
    const [archiveOpen, setArchiveOpen] = useState(false);
    const [requestType, setRequestType] = useState(initialRequest.requestType);

    const handleCloseDialog = () => {
        setIsEditMode(false);
        setFormRequest(initialRequest);
        setRequestType(initialRequest.requestType);
        setSelectedCarrierId("");
    }

    const validateDpc = (dpc) => {
        if (!/^\d{9}$/.test(dpc)) {
            return false;
        }

        // Extract the three portions of the DPC
        const part1 = parseInt(dpc.slice(0, 3), 10);
        const part2 = parseInt(dpc.slice(3, 6), 10);
        const part3 = parseInt(dpc.slice(6, 9), 10);

        // Validate the ranges for each portion
        if (part1 < 0 || part1 > 255) {
            return false;
        }
        if (part2 < 0 || part2 > 255) {
            return false;
        }
        if (part3 < 0 || part3 > 255) {
            return false;
        }

        return true;
    }

    const validateSsn = (ssn) => {
        // Validate is a number
        if (!/^\d{3}$/.test(ssn)) {
            return false;
        }

        // Validate the range for ssn
        if (ssn < 0 || ssn > 255) {
            return false;
        }

        return true;
    }


    const handleUpdate = (name, value, isCheckbox = false, isProvince = false) => {
        setFormRequest(props => { return { ...props, [name]: isCheckbox ? value === "on" : value } });
        if (name === "requestType") {
            setRequestType(value);
        }
    }

    const handleEffectiveDateChange = (value) => {
        setFormRequest(props => { return { ...props, ["blockEffectiveDate"]: value } });
    }

    const handleActivationOptionChange = (value: string) => {
    setFormRequest(prev => ({
        ...prev,
        isActiveBlockRange: value === "isActiveBlockRange",
        soaOrigination: value === "soaOrigination",
        }));
    };


    const [snackbar, setSnackbar] = React.useState<Pick<
        AlertProps,
        "children" | "severity"
    > | null>(null);

    const handleCloseSnackbar = () => setSnackbar(null);

    const handleArchiveClick = (id: string) => () => {
        setRowEditId(id);
        setArchiveOpen(true);
    }

    const handleArchiveCancel = () => {
        setArchiveOpen(false);
    }

    const handleArchiveOk = () => {
        const url = `${appConfig.api.url}/api/formoneb/${rowEditId}/archive`;
        fetch(url, {
            method: "GET",
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
        })
            .then((response) => response.json())
            .then((response) => {
                setArchiveOpen(false);
                const newRows = rows.map(r => r.id === rowEditId ? { ...r, formOneBStatus: FormOneBStatus.Archived } : r);
                setRows(newRows);
            });
    }

    const handlePreviewClick = (id: string) => () => {
        setIsEditMode(true);
        setRowEditId(id);

        const url = `${appConfig.api.url}/api/formoneb/${id}`;
        fetch(url, {
            method: "GET",
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
        })
            .then((response) => response.json())
            .then((response) => {
                setFormRequest(response);
                setRequestType(response.requestType);
                selectCarrier(response.carrierId);
            });
    };

    const selectCarrier = (id: any) => {
        const carrier = carriers.find((c: any) => c.id === id);
        const carrierName = carrier ? carrier["name"] : "N/A";
        setFormRequest(props => {
            return { ...props, ["carrierId"]: id, "entityName": carrierName, "contactName": carrierName, "carrierName": carrierName }
        });
        setSelectedCarrierId(id);
    }

    useEffect(() => {
        setLoading(true);
        instance
            .acquireTokenSilent(loginRequest)
            .then(function (accessTokenResponse) {
                // Acquire token interactive success
                setAccessToken(accessTokenResponse.accessToken);
                // check Role
                const role = getAppRole(accessTokenResponse.accessToken);
                if (role !== AppRole.Administrator) {
                    instance.logoutRedirect();
                }

                fetch(`${appConfig.api.url}/api/formoneb`, {
                    method: "GET",
                    headers: {
                        Authorization: `Bearer ${accessTokenResponse.accessToken}`,
                    },
                })
                    .then((response) => response.json())
                    .then((response) => {
                        setRows(response);
                        setLoading(false);
                    });

                if (carriers && carriers.length == 0) {
                    fetch(`${appConfig.api.url}/api/carriers`, {
                        method: "GET",
                        headers: {
                            Authorization: `Bearer ${accessTokenResponse.accessToken}`,
                        },
                    })
                        .then((response) => response.json())
                        .then((response) => {
                            setCarriers(response);
                        });
                }
            })
            .catch((error) => {
                console.log(error);
                setLoading(false);
            });
    }, [instance]);

    return (
        <>
            <AuthenticatedTemplate>
                <Box sx={{ height: 700, width: "100%" }}>
                    <DataGrid
                        rows={rows}
                        columns={columns}
                        initialState={{
                            pagination: {
                                paginationModel: {
                                    pageSize: 10,
                                },
                            },
                            sorting: {
                                sortModel: [{ field: 'createdAt', sort: 'desc' }]
                            }
                        }}
                        pageSizeOptions={[20]}
                        disableRowSelectionOnClick
                        getRowClassName={(params) => `row-${params.row.formOneBStatus}`}
                        loading={loading}
                        slots={{
                            toolbar: AddToolbar as GridSlots["toolbar"],
                            footer: CustomDataGridFooter as GridSlots["footer"],
                        }}
                        slotProps={{
                            toolbar: { setRowEditId, setIsEditMode },
                        }}
                    />
                </Box>
                <Dialog
                    open={isEditMode}
                    onClose={handleCloseDialog}
                    className="form dialog"
                    PaperProps={{
                        component: 'form',
                        onSubmit: (event: React.FormEvent<HTMLFormElement>) => {
                            event.preventDefault();

                            const input = document.querySelector(
                                `input[aria-invalid="true"]`
                            ) as HTMLElement;

                            if (input) {
                                input.scrollIntoView({
                                    behavior: 'smooth',
                                    block: 'center',
                                    inline: 'start',
                                });
                                input.focus();
                                return;
                            }

                            // SOA / NPAC radio-style validation
                            if (!formRequest["isActiveBlockRange"] && !formRequest["soaOrigination"]) {
                                setSnackbar({
                                    children: "Please select either \"NPAC Activation\" or \"SOA Activation\".",
                                    severity: "error"
                                });
                                return;
                            }

                            // We shouldn't fall into this but it's a good safety
                            if (formRequest["isActiveBlockRange"] && formRequest["soaOrigination"]) {
                                setSnackbar({
                                    children: "Please select only one of \"NPAC Activation\" or \"SOA Activation\".",
                                    severity: "error"
                                });
                            }

                            let data = formRequest;
                            if (!formRequest["blockEffectiveDate"]) {
                                data["blockEffectiveDate"] = dayjs(new Date()).add(effectiveDateAddDays, 'day');
                            }
                            const url = rowEditId ?
                                `${appConfig.api.url}/api/formoneb/${rowEditId}` :
                                `${appConfig.api.url}/api/formoneb`;
                            const method = rowEditId ? "PUT" : "POST";
                            if (!rowEditId) {
                                data["id"] = uuid();
                            }

                            fetch(url, {
                                method: method,
                                headers: {
                                    Authorization: `Bearer ${accessToken}`,
                                    "Content-Type": "application/json",
                                },
                                body: JSON.stringify(data),
                            })
                                .then((response) => response.json())
                                .then((response) => {
                                    if (response.referenceNumber) {
                                        setIsEditMode(false);

                                        // If adding new carrier add new row to the grid 
                                        if (!rowEditId) {
                                            setRows((oldRows) => [
                                                ...oldRows,
                                                {
                                                    id: formRequest["id"],
                                                    referenceNumber: response.referenceNumber,
                                                    name: formRequest["name"],
                                                    groupId: formRequest["groupId"],
                                                    entityName: formRequest["entityName"],
                                                    blockRange: formRequest["blockRange"],
                                                    formOneBStatus: 0,
                                                    createdAt: new Date(),
                                                },
                                            ]);
                                        }
                                        if (rowEditId && formRequest["formOneBStatus"] == FormOneBStatus.Queued) {
                                            const newRows = rows.map(r => r.id === rowEditId ? {
                                                ...r,
                                                name: formRequest["name"],
                                                groupId: formRequest["groupId"],
                                                entityName: formRequest["entityName"],
                                                blockRange: formRequest["blockRange"]
                                            } : r);
                                            setRows(newRows);
                                        }
                                        setFormRequest(initialRequest);
                                        setSelectedCarrierId("");
                                        setRequestType(initialRequest.requestType);
                                        setSnackbar({
                                            children: "Request successfully saved",
                                            severity: "success",
                                        });
                                    } else {
                                        setSnackbar({ children: "Failed to save data", severity: "error" });
                                    }
                                })
                                .catch((error) => {
                                    setSnackbar({ children: error.message, severity: "error" });
                                });
                        },
                    }}
                >
                    <DialogTitle>{rowEditId ? "View" : "Add"}</DialogTitle>
                    <DialogContent>
                        {/* Section A */}
                        <Typography variant="h6">Section A</Typography>
                        <Grid container spacing={1} className="container">
                            <Grid item xs={3}>
                                <InputLabel>Type of Request:</InputLabel>
                                <Select name="requestType" value={requestType} disabled={rowEditId ? true : false}
                                    onChange={(e) => handleUpdate(e.target.name, e.target.value)}>
                                    <MenuItem value="Activation" key="Activation">Activation Request</MenuItem>
                                    <MenuItem value="Modification" key="Modification">Modification Request</MenuItem>
                                    <MenuItem value="Porting" key="Porting">Intra SP Block Porting Request</MenuItem>
                                    <MenuItem value="Information" key="Information">For Information Only</MenuItem>
                                </Select>
                            </Grid>
                            {rowEditId && (
                                <Grid item xs={9}>
                                    <InputLabel>Status:</InputLabel>
                                    <TextField
                                        disabled
                                        value={FormOneBStatus[formRequest["formOneBStatus"]]}
                                        fullWidth
                                    />
                                </Grid>)}
                            <PoolingAdministrator></PoolingAdministrator>
                            <Grid item xs={12}>
                                <InputLabel>Block Applicant:</InputLabel>
                                <Select
                                    required
                                    name="carrierId"
                                    disabled={rowEditId && formRequest["formOneBStatus"] != FormOneBStatus.Queued ? true : false}
                                    value={selectedCarrierId}
                                    onChange={(e) => selectCarrier(e.target.value)}
                                    fullWidth
                                >
                                    {carriers.map((c: any) => (
                                        <MenuItem value={c.id} key={c.name}>{c.name}</MenuItem>
                                    ))}
                                </Select>
                            </Grid>
                            <Grid item xs={6}>
                                <InputLabel>Service Provider NPAC SOA SPID</InputLabel>
                                <TextField
                                    required
                                    error={formRequest["spid"] && !/^[A-Za-z0-9]{4}$/.test(formRequest["spid"])}
                                    name="spid"
                                    disabled={rowEditId && formRequest["formOneBStatus"] != FormOneBStatus.Queued ? true : false}
                                    value={formRequest["spid"]}
                                    onChange={(e) => handleUpdate(e.target.name, e.target.value)}
                                    fullWidth
                                    inputProps={{ maxLength: 4 }}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <InputLabel>LRN</InputLabel>
                                <TextField
                                    required
                                    name="lrn"
                                    error={formRequest["lrn"] && !/^\d{10}$/.test(formRequest["lrn"])}
                                    value={formRequest["lrn"]}
                                    disabled={rowEditId && formRequest["formOneBStatus"] != FormOneBStatus.Queued ? true : false}
                                    inputProps={{ maxLength: 10 }}
                                    onChange={(e) => handleUpdate(e.target.name, e.target.value)}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <InputLabel>Block (1K) Range</InputLabel>
                                <InputMask
                                    mask="999-999-9" maskChar="x"
                                    error={formRequest["blockRange"] && !/^\d{7}$/.test(formRequest["blockRange"])}
                                    inputProps={{ maxLength: 7 }}
                                    value={formRequest["blockRange"]}
                                    disabled={rowEditId && formRequest["formOneBStatus"] != FormOneBStatus.Queued ? true : false}
                                    onChange={(e) => handleUpdate(e.target.name, e.target.value)}
                                >
                                    {() => (
                                        <TextField
                                            name="blockRange"
                                            fullWidth
                                            required
                                        />
                                    )}
                                </InputMask>
                            </Grid>
                            <Grid item xs={6}>
                                <InputLabel>Block Effective Date</InputLabel>
                                {rowEditId ?
                                    (<DatePicker
                                        name="blockEffectiveDate"
                                        disabled={formRequest["formOneBStatus"] != FormOneBStatus.Queued ? true : false}
                                        onChange={(e) => handleEffectiveDateChange(e)}
                                        minDate={dayjs(new Date()).add(effectiveDateAddDays, 'day')}
                                        value={dayjs(formRequest["blockEffectiveDate"])}
                                    />) : (
                                        <DatePicker
                                            name="blockEffectiveDate"
                                            onChange={(e) => handleEffectiveDateChange(e)}
                                            minDate={dayjs(new Date()).add(effectiveDateAddDays, 'day')}
                                            defaultValue={dayjs(new Date()).add(effectiveDateAddDays, 'day')}
                                        />)}
                            </Grid>
                            <Grid item xs={12}>
                                {rowEditId && formRequest["formOneBStatus"] != FormOneBStatus.Queued ? (
                                    <InputLabel>
                                        Block being allocated back to the Code Holder on the switch where the NXX Code Resides: {formRequest["isBlockAllocatedToCodeHolder"] ? "Yes" : "No"}
                                    </InputLabel>
                                ) : (
                                    <FormControlLabel
                                        control={<Checkbox name="isBlockAllocatedToCodeHolder"
                                            onChange={(e) => handleUpdate(e.target.name, e.target.value, true)} />}
                                        label="Is Block being allocated back to the Code Holder on the switch where the NXX Code Resides?"
                                    />)}
                            </Grid>
                            <Grid item xs={12}>
                                {rowEditId && formRequest["formOneBStatus"] != FormOneBStatus.Queued ? (
                                    <>
                                        <InputLabel>
                                            NPAC Activation: {formRequest["isActiveBlockRange"] ? "Yes" : "No"}
                                        </InputLabel>
                                        <InputLabel>
                                            SOA Activation: {formRequest["soaOrigination"] ? "Yes" : "No"}
                                        </InputLabel>
                                    </>
                                ) : (
                                    <FormControl component="fieldset">
                                        <FormLabel component="legend">NPAC / SOA Activation</FormLabel>
                                        <RadioGroup
                                            row
                                            name="activationOption"
                                            value={
                                                formRequest["isActiveBlockRange"]
                                                    ? "isActiveBlockRange"
                                                    : formRequest["soaOrigination"]
                                                        ? "soaOrigination"
                                                        : ""
                                            }
                                            onChange={(e) => handleActivationOptionChange(e.target.value)}
                                        >
                                            <FormControlLabel
                                                value="isActiveBlockRange"
                                                control={<Radio />}
                                                label="NPAC Activation"
                                            />
                                            <FormControlLabel
                                                value="soaOrigination"
                                                control={<Radio />}
                                                label="SOA Activation"
                                            />
                                        </RadioGroup>
                                    </FormControl>
                                )}
                            </Grid>
                        </Grid>
                        {/* Section B */}
                        <Typography variant="h6">Section B</Typography>
                        <Grid container spacing={1} className="container">
                            <Grid item xs={3}>
                                <InputLabel>Class DPC</InputLabel>
                                <TextField
                                    name="classDPC"
                                    disabled={rowEditId && formRequest["formOneBStatus"] != FormOneBStatus.Queued ? true : false}
                                    error={formRequest["classDPC"] && !validateDpc(formRequest["classDPC"])}
                                    value={formRequest["classDPC"]}
                                    inputProps={{ maxLength: 9 }}
                                    onChange={(e) => handleUpdate(e.target.name, e.target.value)}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <InputLabel>Class SSN</InputLabel>
                                <TextField
                                    name="classSSN"
                                    disabled={rowEditId && formRequest["formOneBStatus"] != FormOneBStatus.Queued ? true : false}
                                    error={formRequest["classSSN"] && !validateSsn(formRequest["classSSN"])}
                                    value={formRequest["classSSN"]}
                                    inputProps={{ maxLength: 3 }}
                                    onChange={(e) => handleUpdate(e.target.name, e.target.value)}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <InputLabel>LIDB DPC</InputLabel>
                                <TextField
                                    error={formRequest["lidbDPC"] && !validateDpc(formRequest["lidbDPC"])}
                                    name="lidbDPC"
                                    disabled={rowEditId && formRequest["formOneBStatus"] != FormOneBStatus.Queued ? true : false}
                                    inputProps={{ maxLength: 9 }}
                                    value={formRequest["lidbDPC"]}
                                    onChange={(e) => handleUpdate(e.target.name, e.target.value)}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <InputLabel>LIDB SSN</InputLabel>
                                <TextField
                                    name="lidbSSN"
                                    disabled={rowEditId && formRequest["formOneBStatus"] != FormOneBStatus.Queued ? true : false}
                                    error={formRequest["lidbSSN"] && !validateSsn(formRequest["lidbSSN"])}
                                    value={formRequest["lidbSSN"]}
                                    inputProps={{ maxLength: 3 }}
                                    onChange={(e) => handleUpdate(e.target.name, e.target.value)}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <InputLabel>CNAM DPC</InputLabel>
                                <TextField
                                    error={formRequest["cnamDPC"] && !validateDpc(formRequest["cnamDPC"])}
                                    name="cnamDPC"
                                    disabled={rowEditId && formRequest["formOneBStatus"] != FormOneBStatus.Queued ? true : false}
                                    inputProps={{ maxLength: 9 }}
                                    value={formRequest["cnamDPC"]}
                                    onChange={(e) => handleUpdate(e.target.name, e.target.value)}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <InputLabel>CNAM SSN</InputLabel>
                                <TextField
                                    name="cnamSSN"
                                    disabled={rowEditId && formRequest["formOneBStatus"] != FormOneBStatus.Queued ? true : false}
                                    error={formRequest["cnamSSN"] && !validateSsn(formRequest["cnamSSN"])}
                                    value={formRequest["cnamSSN"]}
                                    inputProps={{ maxLength: 3 }}
                                    onChange={(e) => handleUpdate(e.target.name, e.target.value)}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <InputLabel>ISVM DPC</InputLabel>
                                <TextField
                                    error={formRequest["isvmDPC"] && !validateDpc(formRequest["isvmDPC"])}
                                    name="isvmDPC"
                                    disabled={rowEditId && formRequest["formOneBStatus"] != FormOneBStatus.Queued ? true : false}
                                    inputProps={{ maxLength: 9 }}
                                    value={formRequest["isvmDPC"]}
                                    onChange={(e) => handleUpdate(e.target.name, e.target.value)}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <InputLabel>ISVM SSN</InputLabel>
                                <TextField
                                    name="isvmSSN"
                                    disabled={rowEditId && formRequest["formOneBStatus"] != FormOneBStatus.Queued ? true : false}
                                    error={formRequest["isvmSSN"] && !validateSsn(formRequest["isvmSSN"])}
                                    value={formRequest["isvmSSN"]}
                                    inputProps={{ maxLength: 3 }}
                                    onChange={(e) => handleUpdate(e.target.name, e.target.value)}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <InputLabel>WSMSC DPC</InputLabel>
                                <TextField
                                    error={formRequest["wsmscDPC"] && !validateDpc(formRequest["wsmscDPC"])}
                                    name="wsmscDPC"
                                    disabled={rowEditId && formRequest["formOneBStatus"] != FormOneBStatus.Queued ? true : false}
                                    inputProps={{ maxLength: 9 }}
                                    value={formRequest["wsmscDPC"]}
                                    onChange={(e) => handleUpdate(e.target.name, e.target.value)}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <InputLabel>WSMSC SSN</InputLabel>
                                <TextField
                                    name="wsmscSSN"
                                    disabled={rowEditId && formRequest["formOneBStatus"] != FormOneBStatus.Queued ? true : false}
                                    error={formRequest["wsmscSSN"] && !validateSsn(formRequest["wsmscSSN"])}
                                    value={formRequest["wsmscSSN"]}
                                    inputProps={{ maxLength: 3 }}
                                    onChange={(e) => handleUpdate(e.target.name, e.target.value)}
                                    fullWidth
                                />
                            </Grid>
                        </Grid>
                        {/* Section C */}
                        {rowEditId && (
                            <>
                                <Typography variant="h6">Section C</Typography>
                                <Grid container spacing={1} className="container">
                                    <Grid item xs={4}>
                                        <InputLabel>Type:</InputLabel> {formRequest["requestType"]}
                                    </Grid>
                                    <Grid item xs={8}>
                                        <InputLabel>Status:</InputLabel> {FormOneBStatus[formRequest["formOneBStatus"]]}
                                    </Grid>
                                    <Grid item xs={4}>
                                        <InputLabel>Block (1K) Request Complete:</InputLabel>
                                        {formRequest["completeDate"] ? "Yes" : "No"}
                                    </Grid>
                                    <Grid item xs={4}>
                                        <InputLabel>Complete Date:</InputLabel> {formRequest["completeDate"] ? dayjs(formRequest["completeDate"]).format("YYYY-MM-DD hh:mm A") : ""}
                                    </Grid>
                                    <Grid item xs={4}>
                                        <InputLabel>NPAC Personal performing change:</InputLabel> {formRequest["npacPersonalName"]}
                                    </Grid>
                                    <Grid item xs={4}>
                                        <InputLabel>Block Holder sent Completed Form:</InputLabel>
                                        {formRequest["blockHolderComplitionFormSentDate"] ? "Yes" : "No"}
                                    </Grid>
                                    <Grid item xs={4}>
                                        <InputLabel>Mailed Date:</InputLabel> {formRequest["blockHolderComplitionFormSentDate"] ? dayjs(formRequest["blockHolderComplitionFormSentDate"]).format("YYYY-MM-DD hh:mm A") : ""}
                                    </Grid>
                                    <Grid item xs={4}>
                                        <InputLabel>Contact Name:</InputLabel> {formRequest["completionFormContactName"]}
                                    </Grid>
                                    <Grid item xs={4}>
                                        <InputLabel>Pooling Administrator sent Completed Form:</InputLabel>
                                        {formRequest["paComplitionFormSentDate"] ? "Yes" : "No"}
                                    </Grid>
                                    <Grid item xs={4}>
                                        <InputLabel>Mailed Date:</InputLabel> {formRequest["paComplitionFormSentDate"] ? dayjs(formRequest["paComplitionFormSentDate"]).format("YYYY-MM-DD hh:mm A") : ""}
                                    </Grid>
                                    <Grid item xs={4}>
                                        <InputLabel>Contact Name:</InputLabel> {formRequest["pACompletionFormContactName"]}
                                    </Grid>
                                    <Grid item xs={12}>
                                        <InputLabel>Remarks:</InputLabel> {formRequest["remarks"]}
                                    </Grid>
                                </Grid>
                            </>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseDialog}>Cancel</Button>
                        {rowEditId && formRequest["formOneBStatus"] != FormOneBStatus.Received &&
                            (
                                <Button onClick={handleArchiveClick(rowEditId)}>Archive</Button>
                            )}
                        {(!rowEditId || (rowEditId && formRequest["formOneBStatus"] == FormOneBStatus.Queued)) &&
                            (
                                <Button type="submit">Save</Button>
                            )}
                    </DialogActions>
                </Dialog>
                <Dialog
                    sx={{ '& .MuiDialog-paper': { width: '80%', maxHeight: 435 } }}
                    maxWidth="xs"
                    open={archiveOpen}
                >
                    <DialogTitle>Confirmation required</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            Are you sure you want to archive this request?
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleArchiveOk}>Ok</Button>
                        <Button autoFocus onClick={handleArchiveCancel}>
                            Cancel
                        </Button>
                    </DialogActions>
                </Dialog>
                {!!snackbar && (
                    <Snackbar
                        open
                        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                        onClose={handleCloseSnackbar}
                        autoHideDuration={6000}
                    >
                        <Alert {...snackbar} onClose={handleCloseSnackbar} />
                    </Snackbar>
                )}
            </AuthenticatedTemplate >
        </>
    )
}